import { useEffect, useRef, useState } from 'react';
import { initializeDevice, cleanupDevice, setIncomingCallHandler } from '../services/twilioService';
import { getTwilioToken } from '../services/api';
import { useAuth } from './useAuth';

/**
 * @param {Object} [options]
 * @param {(call: import('@twilio/voice-sdk').Call) => void} [options.onIncomingCall]
 *   Invoked when Twilio pushes an inbound call to this device. The handler owns the decision
 *   to accept or reject — the SDK does nothing on its own.
 */
export const useTwilioDevice = ({ onIncomingCall } = {}) => {
  const [device, setDevice] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const { isAuthenticated } = useAuth();

  // Keep the newest handler in a ref so a caller passing an unmemoized callback can never
  // tear down and re-register the device mid-call.
  const onIncomingCallRef = useRef(onIncomingCall);
  useEffect(() => {
    onIncomingCallRef.current = onIncomingCall;
  }, [onIncomingCall]);

  // Registered independently of device initialization: the service holds the handler at module
  // scope and reads it when the event fires, so ordering between the two doesn't matter.
  useEffect(() => {
    setIncomingCallHandler((call) => onIncomingCallRef.current?.(call));
    return () => setIncomingCallHandler(null);
  }, []);

  // Track user interaction to satisfy autoplay policy
  useEffect(() => {
    if (hasUserInteracted) {
      console.log('[useTwilioDevice] User has already interacted');
      return; // Already interacted, no need to listen
    }

    console.log('[useTwilioDevice] Listening for user interaction...');

    const handleInteraction = () => {
      console.log('[useTwilioDevice] User interaction detected!');
      setHasUserInteracted(true);
    };

    document.addEventListener('click', handleInteraction);
    document.addEventListener('touchstart', handleInteraction);

    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, [hasUserInteracted]);

  useEffect(() => {
    const token = getTwilioToken();

    console.log('[useTwilioDevice] Init effect triggered:', {
      token: !!token,
      isAuthenticated,
      hasUserInteracted,
    });

    if (!token || !isAuthenticated || !hasUserInteracted) {
      console.log('[useTwilioDevice] Skipping init - missing conditions');
      if (isInitializing) {
        setIsInitializing(false);
      }
      return;
    }

    console.log('[useTwilioDevice] All conditions met, initializing device...');

    const initDevice = async () => {
      try {
        setIsInitializing(true);
        setError(null);
        console.log('[useTwilioDevice] Starting device initialization...');
        const initializedDevice = await initializeDevice(token);
        console.log('[useTwilioDevice] Device initialized successfully:', initializedDevice);
        setDevice(initializedDevice);
      } catch (err) {
        console.error('[useTwilioDevice] Failed to initialize device:', err);
        setError(err);
      } finally {
        setIsInitializing(false);
      }
    };

    initDevice();

    return () => {
      cleanupDevice();
      setDevice(null);
    };
  }, [isAuthenticated, hasUserInteracted]);

  return {
    device,
    isReady: !isInitializing && device !== null,
    isLoading: isInitializing,
    error,
  };
};
